/**
 * Password policy, as the "defina sua senha" screen shows it (first login).
 *
 * ⚠️ **The policy lives on the Cognito user pool, and the pool does not exist yet**
 * (`CONEXÃO`, 14/ago+). The defaults below are **Cognito's own defaults** for a new
 * user pool — not a policy we invented, and not one iOX has stated. They are read
 * from `config.json` so the screen can be aligned with the real pool without a
 * rebuild, and **must be confirmed against the pool the day it is provisioned**
 * (§9).
 *
 * What is checked here is a **courtesy**, exactly like permission gating (BR-17):
 * it turns a round-trip into instant feedback. **Cognito is authoritative** — if it
 * rejects a password this module accepted, `InvalidPasswordException` comes back and
 * the user sees the pt-BR message for it.
 */

export interface PoliticaDeSenha {
  minimoCaracteres: number
  exigeMaiuscula: boolean
  exigeMinuscula: boolean
  exigeNumero: boolean
  exigeEspecial: boolean
}

/** Cognito's defaults for a freshly created user pool. */
export const POLITICA_PADRAO: PoliticaDeSenha = {
  minimoCaracteres: 8,
  exigeMaiuscula: true,
  exigeMinuscula: true,
  exigeNumero: true,
  exigeEspecial: true,
}

export interface RequisitoDeSenha {
  id: string
  /** pt-BR, imperative, matching the rest of the screen's copy. */
  texto: string
  atendido: boolean
}

/** The character set Cognito accepts as "special" — its list, not ours. */
const ESPECIAIS = /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`+=-]/

/**
 * Every requirement plus whether the candidate meets it, so the screen can show the
 * whole list from the start and tick items live. A password rejected one rule at a
 * time is a password typed four times.
 */
export function avaliarSenha(senha: string, politica: PoliticaDeSenha): RequisitoDeSenha[] {
  const requisitos: RequisitoDeSenha[] = [
    {
      id: 'tamanho',
      texto: `Pelo menos ${politica.minimoCaracteres} caracteres`,
      atendido: senha.length >= politica.minimoCaracteres,
    },
  ]

  if (politica.exigeMaiuscula) {
    requisitos.push({
      id: 'maiuscula',
      texto: 'Uma letra maiúscula',
      atendido: /[A-ZÀ-Þ]/.test(senha),
    })
  }
  if (politica.exigeMinuscula) {
    requisitos.push({
      id: 'minuscula',
      texto: 'Uma letra minúscula',
      atendido: /[a-zß-ÿ]/.test(senha),
    })
  }
  if (politica.exigeNumero) {
    requisitos.push({ id: 'numero', texto: 'Um número', atendido: /\d/.test(senha) })
  }
  if (politica.exigeEspecial) {
    requisitos.push({
      id: 'especial',
      texto: 'Um caractere especial (por exemplo ! @ # $ %)',
      atendido: ESPECIAIS.test(senha),
    })
  }

  return requisitos
}

export function senhaAtendePolitica(senha: string, politica: PoliticaDeSenha): boolean {
  return avaliarSenha(senha, politica).every((requisito) => requisito.atendido)
}
